import { TokenValues } from "@silversea-ssc/dotcom-read-model-types/Tokens";
import {
  BlockContentValue,
  PartialSanityImageType
} from "@silversea-ssc/ui-components-react";
import NativeHeroImage from "components/NativeHeroImage/NativeHeroImage";
import BackgroundVideo from "components/Video/BackgroundVideo";
import React, { PropsWithChildren, useMemo } from "react";
import * as styles from "./EmotionalHeroBanner.module.scss";
import EmotionalHeroBannerContent from "./EmotionalHeroBannerContent";
import clsx from "clsx";

type BaseProps = {
  title: string | BlockContentValue | undefined;
  content: string | BlockContentValue | undefined;
  ctaButtons: React.ReactNode;
  backgroundImage?: PartialSanityImageType | undefined;
  videoId?: string;
  countDownLabel?: string;
  showCountdown?: boolean;
  countdownDate?: Date;
  tokenValues?: TokenValues;
  mobileVideoId?: string;
  trackingVideoTitle?: string;
  mediaType?: string;
  customMargin?: boolean;
};

const EmotionalHeroBanner: React.FC<PropsWithChildren<BaseProps>> = ({
  title,
  content,
  ctaButtons,
  backgroundImage,
  videoId,
  countDownLabel,
  showCountdown,
  countdownDate,
  tokenValues,
  mobileVideoId,
  trackingVideoTitle,
  mediaType,
  customMargin = false,
  children
}) => {
  const contentComponent = useMemo(() => {
    return (
      <>
        <div
          className={clsx(styles.contentContainer, {
            [styles.customMargin]: customMargin
          })}
        >
          <EmotionalHeroBannerContent
            title={title}
            content={content}
            ctaButtons={ctaButtons}
            tokenValues={tokenValues}
            countdownDate={countdownDate}
            countdownLabel={countDownLabel}
            showCountdown={showCountdown}
          />
        </div>
        <>{children}</>
      </>
    );
  }, [
    title,
    content,
    ctaButtons,
    tokenValues,
    countdownDate,
    countDownLabel,
    showCountdown,
    children
  ]);

  if (videoId && mediaType === "video") {
    return (
      <HeroBannerVideo
        videoId={videoId}
        mobileVideoId={mobileVideoId}
        trackingTitle={trackingVideoTitle}
      >
        {contentComponent}
      </HeroBannerVideo>
    );
  }

  return (
    <HeroBannerImage image={backgroundImage}>
      {contentComponent}
    </HeroBannerImage>
  );
};

type HeroBannerImageProps = {
  image: PartialSanityImageType | undefined;
};

const HeroBannerImage: React.FC<PropsWithChildren<HeroBannerImageProps>> = ({
  image,
  children
}) => {
  return (
    <div className={styles.wrapper} data-content-name="hero-banner">
      {image && (
        <div className={styles.backgroundMedia}>
          <NativeHeroImage image={image} alt={"hero image"} lazyload={false} />
        </div>
      )}
      <>{children}</>
    </div>
  );
};

type HeroBannerVideoProps = {
  videoId: string;
  mobileVideoId?: string;
  trackingTitle?: string;
};

const HeroBannerVideo: React.FC<PropsWithChildren<HeroBannerVideoProps>> = ({
  videoId,
  mobileVideoId,
  trackingTitle,
  children
}) => {
  return (
    <div className={styles.wrapper} data-content-name="hero-banner">
      <div className={styles.backgroundMedia}>
        <BackgroundVideo
          videoId={videoId}
          mobileVideoId={mobileVideoId}
          trackingTitle={trackingTitle || "hero video"}
        />
      </div>
      <>{children}</>
    </div>
  );
};

export default EmotionalHeroBanner;
